import React, { useEffect, useState } from "react";
import { useTranslation } from "../../../utils/useTranslation";
import { View, Text } from "../../../components/shared/Themed";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import {
  Dimensions,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
} from "react-native";
import { AntDesign } from "@expo/vector-icons";
import { reserveStockModel } from "../../../models/reportsModel";

interface RouteParams {
  order: reserveStockModel;
}

interface DetailItem {
  label: string;
  value: string;
  supportingValue?: string;
}

interface ReserveStockDetailsScreenProps {
  order?: reserveStockModel;
  onBackPress?: () => void;
}

const ReserveStockDetailsScreen = ({ order: propOrder, onBackPress }: ReserveStockDetailsScreenProps = {}) => {
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const routeOrder = route?.params?.order;
  const order = propOrder || routeOrder || {} as reserveStockModel;
  const { t } = useTranslation();
  const [windowWidth, setWindowWidth] = useState(
    Dimensions.get("window").width
  );
  const navigation = useNavigation();

  // Determine if we're on a small screen (mobile)
  const isMobileView = windowWidth < 768;

  useEffect(() => {
    // Redirect if order is invalid
    if (!order || Object.keys(order).length === 0) {
      navigation.navigate("ReserveStock");
      return;
    }

    // Track screen size changes
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setWindowWidth(window.width);
    });

    // Cleanup on unmount
    return () => subscription?.remove();
  }, [order, navigation]);

  const handleBack = () => {
    if (onBackPress) {
      onBackPress();
    } else {
      navigation.navigate("ReserveStock");
    }
  };

  const DrawerHeader = () => {
    return (
      <View style={styles.drawerHeaderStyle}>
        <View style={styles.headerRow}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={handleBack}
          >
            <AntDesign name="arrowleft" size={24} color="black" />
          </TouchableOpacity>

          <View style={styles.headerTitleContainer}>
            <Text selectable={true} style={styles.headerTitle}>
              {`${t("itemCode")}: ${order?.ItemCode || ""}`}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const ProductDetails = () => {
    const detailItems: DetailItem[] = [
      { label: "Order Number", value: order?.DocNum || "-" },
      { label: "Customer Code", value: order?.CardCode || "-" },
      { label: "Customer Name", value: order?.CardName || "-" },
      { label: "Address", value: order?.Address2 || "-" },
      { label: "Warehouse", value: order?.WhsName || order?.WhsCode || "-" },
      { label: "Item Code", value: order?.ItemCode || "-" },
      { label: "Description", value: order?.Dscription || "-" },
      { 
        label: "Order Reserved", 
        value: order?.["Open Qty PCS"]?.toString() || "0",
        supportingValue: order?.PerCtns ? `${Number(order?.["Open Qty PCS"] || 0) / Number(order?.PerCtns)} CTNs` : undefined
      },
      { label: "Sales", value: order?.SlpName || "-" },
      { label: "Order Date", value: order?.["Order Date"] ? new Date(order?.["Order Date"]).toLocaleDateString('en-GB') : "-" },
      { label: "Valid until", value: order?.["Valid until"] ? new Date(order?.["Valid until"]).toLocaleDateString('en-GB') : "-" },
      { label: "Doc Status", value: order?.["Doc Status"] || "-" },
    ];

    return (
      <View style={styles.productDetailsContainer}>
        <View style={styles.header}>
          <Text selectable={true} style={styles.title}>{order?.ItemCode || ""}</Text>
          <Text selectable={true} style={styles.subtitle}>{order?.Dscription || ""}</Text>
        </View>
        <View style={styles.body}>
          {detailItems.map((item, index) => (
            <View key={index} style={styles.detailRow}>
              <Text selectable={true} style={styles.label}>{item.label}:</Text>
              <Text selectable={true} style={styles.value}>{item.value}</Text>
              {item.supportingValue && (
                <Text selectable={true} style={styles.supporting}>
                  ({item.supportingValue})
                </Text>
              )}
            </View>
          ))}
        </View>
      </View>
    );
  };

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <DrawerHeader />

      <View
        style={[
          styles.contentContainer,
          isMobileView && styles.contentContainerMobile,
        ]}
      >
        <View
          style={[
            styles.orderDetails,
            isMobileView && styles.orderDetailsMobile,
          ]}
        >
          <View style={[styles.detailBox, isMobileView && styles.detailBoxMobile]}>
            <ProductDetails />
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: "#fff",
  },
  contentContainer: {
    padding: 16,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  contentContainerMobile: {
    flexDirection: "column",
  },
  orderDetails: {
    flex: 1,
    maxWidth: "100%",
  },
  orderDetailsMobile: {
    flex: 0,
    maxWidth: "100%",
    marginBottom: 16,
  },
  detailBox: {
    borderRadius: 14,
    padding: 16,
    height: "100%",
  },
  detailBoxMobile: {
    height: "auto",
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  verticalLine: {
    width: 1,
    backgroundColor: "#e3e3e3",
    marginHorizontal: 16,
  },
  drawerHeaderStyle: {
    width: "100%",
    zIndex: 999,
    backgroundColor: "#fff",
    height: 56,
    borderBottomWidth: 1,
    borderColor: "#e0e0e0",
    justifyContent: "center",
    paddingHorizontal: 16,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  backButton: {
    paddingVertical: 8,
    paddingRight: 16,
  },
  headerTitleContainer: {
    flex: 1,
  },
  headerTitle: {
    fontWeight: "700",
    fontSize: 16,
    userSelect: "text" as any,
  },
  // Product Details Styles
  productDetailsContainer: {
    flex: 1,
  },
  header: {
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 4,
    userSelect: "text" as any,
  },
  subtitle: {
    fontSize: 14,
    color: "#666",
    marginBottom: 16,
    userSelect: "text" as any,
  },
  body: {
    flex: 1,
  },
  label: {
    fontWeight: "600",
    fontSize: 14,
    flex: 1,
    userSelect: "text" as any,
  },
  value: {
    fontSize: 14,
    color: "#333",
    flex: 1,
    textAlign: "right",
    userSelect: "text" as any,
  },
  supporting: {
    fontSize: 12,
    color: "#888",
    marginLeft: 8,
    userSelect: "text" as any,
  },
});

export default ReserveStockDetailsScreen;
